<script setup lang="ts">
/**
 * FilterFieldPicker
 *
 * Layer 1 — Simple dropdown for casual filter creation.
 * Shows searchable field list with type icons.
 * "Advanced filters" link at bottom to switch to Layer 2.
 */

import { ref, computed, watch } from 'vue'
import type { SchemaField, FilterOperator, SavedViewEntityType } from '../api/types'
import { DYNAMIC_TOKENS } from '../api/types'
import { savedViewService } from '../api/service'
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue'
import type { MultiSelectOption } from '@/components/MultiSelectDropdown.vue'

interface Props {
    fields: SchemaField[]
    entityType: SavedViewEntityType
}

const props = defineProps<Props>()

const emit = defineEmits<{
    'add-condition': [field: string, op: FilterOperator, value: unknown]
    'open-advanced': []
    close: []
}>()

const searchQuery = ref('')
const selectedField = ref<SchemaField | null>(null)
const selectedOp = ref<FilterOperator>('$eq')
const inputValue = ref<string>('')
const dateTokenSelection = ref<string>('custom')

// Dynamic tokens metadata
const applicableTokens = computed(() => {
    if (!selectedField.value) return []
    const type = selectedField.value.type
    return DYNAMIC_TOKENS.filter(t => t.applicableTypes.includes(type as 'string' | 'relation' | 'date'))
})

const dateTokens = computed(() => DYNAMIC_TOKENS.filter(t => t.applicableTypes.includes('date')))

watch(dateTokenSelection, (newVal) => {
    if (newVal === 'custom') {
        inputValue.value = ''
    } else {
        inputValue.value = newVal
    }
})

// Multi-select support
const relationOptions = ref<MultiSelectOption[]>([])
const selectedRelationIds = ref<string[]>([])
const isLoadingOptions = ref(false)

const filteredFields = computed(() => {
    const q = searchQuery.value.toLowerCase().trim()
    if (!q) return props.fields
    return props.fields.filter(f =>
        f.label.toLowerCase().includes(q) || f.name.toLowerCase().includes(q)
    )
})

const isRelation = computed(() => selectedField.value?.type === 'relation')
const isExistsOp = computed(() => selectedOp.value === '$exists')
const isInOp = computed(() => selectedOp.value === '$in' || selectedOp.value === '$nin')

/** Whether the $in/$nin multi-select should be shown */
const showMultiSelect = computed(() => {
    if (!selectedField.value) return false
    if (!isInOp.value) return false
    return isRelation.value || selectedField.value.type === 'enum' || selectedField.value.type === 'string'
})

function fieldIcon(type: string): string {
    const icons: Record<string, string> = {
        string: 'Aa',
        number: '#',
        boolean: '⊘',
        'virtual-boolean': '⊘',
        enum: '≡',
        date: '📅',
        relation: '🔗',
    }
    return icons[type] || '•'
}

async function loadFieldOptions(fieldName: string) {
    isLoadingOptions.value = true
    try {
        const values = await savedViewService.getFieldValues(props.entityType, fieldName)
        let loadedOptions: MultiSelectOption[] = []
        if (Array.isArray(values) && values.length > 0) {
            if (typeof values[0] === 'string') {
                loadedOptions = (values as string[]).map(v => ({ value: v, label: v }))
            } else {
                loadedOptions = (values as { id: string; label: string }[]).map(v => ({
                    value: v.id,
                    label: v.label,
                }))
            }
        }
        const tokenOpts: MultiSelectOption[] = applicableTokens.value.map(t => ({
            value: t.token,
            label: `⚡ ${t.label}`,
        }))
        relationOptions.value = [...tokenOpts, ...loadedOptions]
    } catch {
        const tokenOpts: MultiSelectOption[] = applicableTokens.value.map(t => ({
            value: t.token,
            label: `⚡ ${t.label}`,
        }))
        relationOptions.value = tokenOpts
    } finally {
        isLoadingOptions.value = false
    }
}

function selectField(field: SchemaField) {
    selectedField.value = field
    selectedOp.value = field.operators[0] || '$eq'
    inputValue.value = ''
    dateTokenSelection.value = 'custom'
    selectedRelationIds.value = []

    // Pre-load options for fields that may use $in
    if (field.type === 'enum' && field.values) {
        // Enum values are already in the schema — no API call needed
        relationOptions.value = field.values.map(v => ({ value: v, label: v }))
    } else if (field.type === 'relation' || field.type === 'string') {
        loadFieldOptions(field.name)
    }
}

// Reload options when operator changes to $in/$nin (in case they weren't loaded yet)
watch(selectedOp, (newOp) => {
    if ((newOp === '$in' || newOp === '$nin') && selectedField.value && relationOptions.value.length === 0) {
        if (selectedField.value.type === 'enum' && selectedField.value.values) {
            relationOptions.value = selectedField.value.values.map(v => ({ value: v, label: v }))
        } else {
            loadFieldOptions(selectedField.value.name)
        }
    }
})

function onBack() {
    selectedField.value = null
    searchQuery.value = ''
    relationOptions.value = []
    selectedRelationIds.value = []
}

function onSubmit() {
    if (!selectedField.value) return

    let value: unknown = inputValue.value
    if (selectedField.value.type === 'number') value = Number(inputValue.value)
    if (selectedField.value.type === 'boolean' || selectedField.value.type === 'virtual-boolean' || selectedOp.value === '$exists') value = inputValue.value === 'true'
    if (showMultiSelect.value) value = [...selectedRelationIds.value]

    emit('add-condition', selectedField.value.name, selectedOp.value, value)

    // Reset
    selectedField.value = null
    searchQuery.value = ''
    inputValue.value = ''
    selectedRelationIds.value = []
    relationOptions.value = []
}

const canApply = computed(() => {
    if (!selectedField.value) return false
    if (showMultiSelect.value) return selectedRelationIds.value.length > 0
    if (selectedField.value.type === 'boolean' || selectedField.value.type === 'virtual-boolean' || isExistsOp.value) return true
    return !!inputValue.value
})

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') emit('close')
    if (e.key === 'Enter' && canApply.value) onSubmit()
}
function getPickerOpLabel(op: FilterOperator): string {
    if (selectedField.value?.type === 'date') {
        const dateLabels: Record<string, string> = {
            $eq: 'is',
            $ne: 'is not',
            $gt: 'is after',
            $gte: 'is on or after',
            $lt: 'is before',
            $lte: 'is on or before',
            $in: 'is any of',
            $nin: 'is none of',
            $exists: 'exists',
        }
        return dateLabels[op] || op
    }

    const labels: Record<string, string> = {
        $eq: 'equals',
        $ne: 'not equals',
        $gt: 'greater than',
        $gte: 'at least',
        $lt: 'less than',
        $lte: 'at most',
        $in: 'is any of',
        $nin: 'is none of',
        $contains: 'contains',
        $regex: 'contains',
        $startsWith: 'starts with',
        $endsWith: 'ends with',
        $exists: 'exists',
    }
    return labels[op] || op
}
</script>

<template>
    <div class="field-picker" @keydown="onKeydown" data-testid="filter-field-picker">
        <!-- Step 1: Field Selection -->
        <template v-if="!selectedField">
            <div class="field-picker__search">
                <input
                    v-model="searchQuery"
                    type="text"
                    class="field-picker__search-input"
                    data-testid="filter-field-search"
                    placeholder="Search fields…"
                    autofocus
                />
            </div>

            <ul class="field-picker__list" role="listbox">
                <li
                    v-for="f in filteredFields"
                    :key="f.name"
                    class="field-picker__item"
                    role="option"
                    tabindex="0"
                    @click="selectField(f)"
                    @keydown.enter="selectField(f)"
                >
                    <span class="field-picker__icon">{{ fieldIcon(f.type) }}</span>
                    <span class="field-picker__label">{{ f.label }}</span>
                    <span v-if="f.type === 'enum'" class="field-picker__tag">enum</span>
                </li>
                <li v-if="filteredFields.length === 0" class="field-picker__empty">
                    No matching fields
                </li>
            </ul>

            <div class="field-picker__footer">
                <button class="field-picker__advanced" @click="emit('open-advanced')">
                    Advanced filters →
                </button>
            </div>
        </template>

        <!-- Step 2: Operator + Value (after field selected) -->
        <template v-else>
            <div class="field-picker__header">
                <button class="field-picker__back" @click="onBack">←</button>
                <span class="field-picker__selected-label">{{ selectedField.label }}</span>
            </div>

            <div class="field-picker__inputs">
                <select v-model="selectedOp" class="field-picker__op">
                    <option
                        v-for="op in selectedField.operators"
                        :key="op"
                        :value="op"
                    >
                        {{ getPickerOpLabel(op) }}
                    </option>
                </select>

                <!-- $in — searchable multi-select (relation or enum) -->
                <MultiSelectDropdown
                    v-if="showMultiSelect"
                    v-model="selectedRelationIds"
                    :options="relationOptions"
                    :disabled="isLoadingOptions"
                    :placeholder="isLoadingOptions ? 'Loading…' : 'Search & select…'"
                    class="field-picker__multi-select"
                />

                <!-- Relation or Boolean: $exists — Yes/No -->
                <select
                    v-else-if="isExistsOp"
                    v-model="inputValue"
                    class="field-picker__val"
                >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>

                <!-- Enum: single select (non-$in) -->
                <select
                    v-else-if="selectedField.type === 'enum' && selectedField.values"
                    v-model="inputValue"
                    class="field-picker__val"
                >
                    <option value="" disabled>Select…</option>
                    <option v-for="v in selectedField.values" :key="v" :value="v">{{ v }}</option>
                </select>

                <!-- Boolean -->
                <select
                    v-else-if="selectedField.type === 'boolean' || selectedField.type === 'virtual-boolean'"
                    v-model="inputValue"
                    class="field-picker__val"
                >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                </select>

                <!-- Date (Supports static date or dynamic tokens: $TODAY, $YESTERDAY, $WEEK, $LAST_WEEK, $MONTH, $LAST_MONTH, $NOW) -->
                <div v-else-if="selectedField.type === 'date'" class="field-picker__date-wrap">
                    <select
                        v-model="dateTokenSelection"
                        class="field-picker__val field-picker__date-token-select"
                    >
                        <option value="custom">Specific Date…</option>
                        <option
                            v-for="t in dateTokens"
                            :key="t.token"
                            :value="t.token"
                        >
                            ⚡ {{ t.label }}
                        </option>
                    </select>
                    <input
                        v-if="dateTokenSelection === 'custom'"
                        v-model="inputValue"
                        type="date"
                        class="field-picker__val"
                    />
                </div>

                <!-- Number -->
                <input
                    v-else-if="selectedField.type === 'number'"
                    v-model="inputValue"
                    type="number"
                    class="field-picker__val"
                    placeholder="Value"
                    autofocus
                />

                <!-- String (default) -->
                <input
                    v-else
                    v-model="inputValue"
                    type="text"
                    class="field-picker__val"
                    placeholder="Value"
                    autofocus
                />
            </div>

            <div class="field-picker__submit">
                <button
                    class="field-picker__apply-btn"
                    :disabled="!canApply"
                    @click="onSubmit"
                >
                    Apply Filter
                </button>
            </div>
        </template>
    </div>
</template>

<style scoped>
.field-picker {
    width: 380px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    overflow: visible;
}

.field-picker__search {
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color);
}

.field-picker__search-input {
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.field-picker__search-input:focus {
    border-color: var(--color-primary);
    outline: none;
}

.field-picker__list {
    max-height: 280px;
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: 0.25rem 0;
}

.field-picker__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    cursor: pointer;
    transition: background 0.1s;
}

.field-picker__item:hover,
.field-picker__item:focus {
    background: var(--bg-hover);
    outline: none;
}

.field-picker__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    font-size: 0.7rem;
    color: var(--text-muted);
}

.field-picker__label {
    flex: 1;
}

.field-picker__tag {
    font-size: 0.625rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    padding: 0.125rem 0.375rem;
    background: var(--bg-surface);
    border-radius: var(--radius-sm);
}

.field-picker__empty {
    padding: 1rem;
    text-align: center;
    font-size: var(--text-sm);
    color: var(--text-muted);
}

.field-picker__footer {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid var(--border-color);
}

.field-picker__advanced {
    font-size: var(--text-xs);
    color: var(--color-primary);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

.field-picker__advanced:hover {
    text-decoration: underline;
}

/* Step 2 styles */
.field-picker__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid var(--border-color);
}

.field-picker__back {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 1rem;
    padding: 0;
}

.field-picker__selected-label {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
}

.field-picker__inputs {
    padding: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.field-picker__op,
.field-picker__val {
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.field-picker__val:focus {
    border-color: var(--color-primary);
    outline: none;
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.field-picker__date-wrap {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    width: 100%;
}

.field-picker__submit {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid var(--border-color);
}

.field-picker__apply-btn {
    width: 100%;
    padding: 0.5rem;
    font-size: var(--text-sm);
    font-weight: 600;
    color: #fff;
    background: var(--color-primary);
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: opacity 0.15s;
}

.field-picker__apply-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.field-picker__apply-btn:not(:disabled):hover {
    opacity: 0.9;
}

/* Ensure MultiSelectDropdown fills the picker width */
.field-picker__multi-select {
    width: 100%;
}

.field-picker__multi-select :deep(.msd__control) {
    min-height: 36px;
}

.field-picker__multi-select :deep(.msd__dropdown) {
    width: 100%;
}
</style>

